/**
 * Limits Model
 */

define(function(require) {
    var _ = require('underscore');
    var BaseModel = require('common/base.model');
    var server = require('server');
    var i18n = require('i18n');
    var user = require('user');
    var limitsConfig = require('portlets/responsible-gaming/limits/limits.config');
    var depositLimitsConfig = require('modules/deposit-limits-config/deposit.limits.config.instance');

    return BaseModel.extend({

        defaults: {
            limit: null,
            isDepositListEnabled: false,
            availableDepositTimePeriods: null,
            depositPredefinedType: ''
        },

        initialize: function() {
            BaseModel.prototype.initialize.apply(this, arguments);

            depositLimitsConfig.fetchDepositLimits(user.get('currencyCode'))
                .done(this.setDepositLimits.bind(this));
        },

        setDepositLimits: function(data) {
            var cfg = data.config;

            this.set({
                allowedLimits: data.limit && data.limit.allowedLimits,
                depositConfig: cfg,
                predefinedLimits: data.limit
            });

            this.fetchInfo();
            this.fetchDepositLimitInfo();
        },

        fetchInfo: function() {
            var isDepositEnabled = this.get('depositConfig').isDepositListEnabled;
            var getInfo = [
                'timeout',
                'selfExclusion',
                'inactivityTimeout',
                'realityCheck',
                'sessionTimer',
                'externalExcluded'
            ].some(this.get.bind(this));

            if (getInfo || isDepositEnabled) {
                return server.send({
                        ID: 31037,
                        description: 'Player limit info',
                        responses: [31038, 31039]
                    })
                    .done(function(response) {
                        this.update(response);
                    }.bind(this));
            }

            this.set('depositlimitallowed', 1);
        },

        fetchDepositLimitInfo: function() {
            var isDepositEnabled = this.get('depositConfig').isDepositListEnabled;

            if (isDepositEnabled) {
                return server.send({
                    ID: 89901,
                    description: 'Get Deposit Limits',
                    correlationId: _.uniqueId('get-deposit-limits'),
                    responses: [89905, 89900]
                })
                .done(function(response) {
                    this.set({ fetchedDepositLimits: this.parse(response) });
                }.bind(this));
            }
        },

        /**
         * Format pending (waiting) deposit limits
         * @param  {Object}     data    model attributes
         * @return {Object}             model attributes
         */
        _formatWaitingData: function(data) {
            // Service array of service objects of keys for automatic formatting in format
            // date:            key for waiting date for day\week\month deposit limits
            // availability:    key for waiting deposit limits availability
            // value:           key for waiting value for day\week\month day\week\month deposit limits
            // period:          period for waiting (day\week\month)
            var waitings = limitsConfig().waiting;
            var waitingLimits = data.waitingLimits;
            const { formatCurrency, getDateTimeShort, parseImsDate } = window.Playtech.API.formatter;

            if (waitingLimits) {
                var waitingDepositLimits = {
                    waitingdaydepositlimit: waitingLimits.find(waitingLimit => waitingLimit.timePeriod === 'daily'),
                    waitingweekdepositlimit: waitingLimits.find(waitingLimit => waitingLimit.timePeriod === 'weekly'),
                    waitingmonthdepositlimit: waitingLimits.find(waitingLimit => waitingLimit.timePeriod === 'monthly')
                };

                _.extend(data, waitingDepositLimits);
            }

            _.each(waitings, waiting => {
                var value = data[waiting.value];
                var amount = value?.amount?.amount;
                var currencyCode = value?.amount?.currencyCode || this.get('defaultCurrency');
                var isValue = value || value === 0;
                var isAmount = amount || amount === 0;

                if (isAmount || isValue) {
                    data[waiting.availability] = true;

                    if (isAmount) {
                        var waitAmountTranslation = Number(amount) >= Number(depositLimitsConfig.UNLIMITED_AMOUNT)
                            ? i18n.t('playtech.mobile.ngresponsiblegaming.limits.unlimited')
                            : formatCurrency(amount, { currencyCode });

                        data[waiting.value] = {
                            date: data[waiting.date].startDate ? getDateTimeShort(parseImsDate(data[waiting.date].startDate)) : '',
                            title: i18n.t('playtech.mobile.ngresponsiblegaming.limits.waiting.' + data[waiting.period].timePeriod, waitAmountTranslation)
                        };
                    } else {
                        var waitValueTranslation = Number(value) >= Number(depositLimitsConfig.UNLIMITED_AMOUNT)
                            ? i18n.t('playtech.mobile.ngresponsiblegaming.limits.unlimited')
                            : formatCurrency(data[waiting.value], { currencyCode });

                        data[waiting.value] = {
                            date: data[`${waiting.date}date`] ? getDateTimeShort(data[`${waiting.date}date`]) : '',
                            title: i18n.t('playtech.mobile.ngresponsiblegaming.limits.waiting.' + waiting.period, waitValueTranslation)
                        };
                    }

                    data[waiting.value] = {
                        ...data[waiting.value],
                        timePeriod: value?.timePeriod,
                        confirmationNeeded: value?.confirmationNeeded
                    };
                }
            });

            ['waitingdaydepositlimit', 'waitingweekdepositlimit', 'waitingmonthdepositlimit'].forEach(limit => {
                if (data[limit]) {
                    data[limit].isDepositType = true;
                }
            });

            return data;
        },

        _parseSessionData: function(data) {
            var sessionData = {};

            if (data.sessionduration) {
                sessionData.waitingsessionduration = data.sessionduration;
            }
            if (data.sessiondurationperiod) {
                sessionData.sessiondurationperiod = data.sessiondurationperiod.replace(/\s/g, '').split(',');
            }

            return sessionData;
        },

        _parseUnlimited: function(data) {
            // issue Number('9223372036854775807') = 9223372036854776000 out of Number.MAX_SAFE_INTEGER
            var convertToString = function(period) {
                return data[period] === Number(depositLimitsConfig.UNLIMITED_AMOUNT)
                    ? depositLimitsConfig.UNLIMITED_AMOUNT
                    : data[period];
            };
            data.daydepositlimit = convertToString('daydepositlimit');
            data.weekdepositlimit = convertToString('weekdepositlimit');
            data.monthdepositlimit = convertToString('monthdepositlimit');

            return data;
        },

        parse: function(response) {
            var data = _.clone(response.data);
            var defaultData = limitsConfig().defaultData;

            _.defaults(data, defaultData); // if some keys are undefined

            _.extend(
                data,
                this._parseSessionData(data),
                this._parseUnlimited(data),
                { defaultCurrency: user.get('currencyCode') }
            );

            var currentLimits = data.currentLimits;

            if (currentLimits) {
                var currentDepositLimits = {
                    currentDayDepositLimit: currentLimits.find(depositLimit => depositLimit.timePeriod === 'daily') || { amount: { amount: 0 } },
                    currentWeekDepositLimit: currentLimits.find(depositLimit => depositLimit.timePeriod === 'weekly') || { amount: { amount: 0 } },
                    currentMonthDepositLimit: currentLimits.find(depositLimit => depositLimit.timePeriod === 'monthly') || { amount: { amount: 0 } }
                };

                _.extend(data, currentDepositLimits);
            }

            data = this._formatWaitingData(data);

            return data;
        },

        updateSessionTimer: function(data) {
            return server.send({
                ID: 31048,
                description: 'Update session timer',
                responses: [31049, 31039],
                sessionDuration: data.sessionduration || null,
                sessionDurationAction: 'promptmessage' // values: 1 – Automatic logout, 2 – Prompt message
            }).done(function(response) {
                user.set({
                    sessionStartTime: Date.now(),
                    sessionLimit: response.data.sessionduration
                });

                this.update(response);
            }.bind(this));
        },

        updateDepositLimits: function(data) {
            return server.send({
                ID: 89904,
                description: 'Set Player Deposit Limits',
                correlationId: 'update-deposit-limit',
                responses: [89908, 89900],
                limits: this.getDepositLimitsPeriodData(data)
            });
        },

        getDepositLimitsPeriodData: function(data) {
            const limits = [];
            const typeMap = {
                daydepositlimit: 'daily',
                weekdepositlimit: 'weekly',
                monthdepositlimit: 'monthly'
            };
            const { currentLimits } = this.get('fetchedDepositLimits');

            for (const key in data) {
                const amount = data[key];
                const timePeriod = typeMap[key];
                const currentLimit = currentLimits.find(obj => obj.timePeriod === timePeriod);

                if (currentLimit?.amount?.amount !== amount ||
                    !currentLimit && amount !== '0') {
                    limits.push({
                        amount: amount,
                        timePeriod: timePeriod
                    });
                }
            }

            return limits;
        },

        update: function(response) {
            this.set(this.parse(response));
        },

        acceptPlayerDepositLimits: function(action, timePeriod) {
            return server.send({
                ID: 89902,
                description: 'Accept Player Deposit Limits',
                responses: [89906, 89900],
                action,
                timePeriods: [timePeriod]
            });
        }
    });
});
